import {
  Card,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  message,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import axios from 'axios';
import { celularMask, cepMask, cleanMask, cnpjMask } from 'masks-br';
import React from 'react';
import SearchCEP from '../../../../services/SearchCEP';
import {
  options_horario_funcionamento,
  options_tipo_tecnologia,
} from './constants';

const { Title } = Typography;

interface PropsFormJuridico {
  form: FormInstance<any>;
  estadoOptions: any[];
  handleFetchCidade: (state: string, ibge: string) => void;
  default_message: string;
  ramoAtividadeOptions: any[];
  loading: any;
  newRamoAtividade: string;
  setNewRamoAtividade: React.Dispatch<React.SetStateAction<string>>;
  handleStoreRamoAtividade: () => void;
}

const FormJuridico = ({
  form,
  estadoOptions,
  handleFetchCidade,
  default_message,
  ramoAtividadeOptions,
  loading,
  newRamoAtividade,
  setNewRamoAtividade,
  handleStoreRamoAtividade,
}: PropsFormJuridico) => {
  async function handlFetchCEP(cep: string) {
    form.setFieldsValue({ cep: cepMask(cep) });

    if (cep.length !== 9) return;

    message.loading({ content: 'Buscando...', duration: 10000, key: 'cep' });

    const fetched_info = await SearchCEP(cleanMask(cep));

    message.destroy();

    const find_estado = estadoOptions.find(
      (val: any) => val.sigla === fetched_info.uf,
    )?.value;

    handleFetchCidade(find_estado, fetched_info.ibge);

    form.setFieldsValue({
      endereco: fetched_info.logradouro,
      bairro: fetched_info.bairro,
      id_estado: find_estado,
    });
  }

  async function handleFetchCNPJ(cnpj: any) {
    const key = String(Math.random());

    message.loading({
      type: 'loading',
      content: 'Buscando informações de empresa, aguarde...',
      key,
      duration: 100000,
    });

    return axios
      .get(`https://www.receitaws.com.br/v1/cnpj/${cnpj}`)
      .then(({ data }) => {
        if (data.cep) {
          handlFetchCEP(cleanMask(data.cep));
        }

        form.setFieldsValue({
          nome: data.fantasia || data.nome,
          razao_social: data.nome,
          cep: cepMask(cleanMask(data.cep)),
          numero: data.numero,
          telefone: celularMask(cleanMask(data.telefone)),
          complemento: data.complemento,
          email: data.email,
        });

        message.destroy(key);
      })
      .catch(() => {
        message.info({
          type: 'info',
          content: 'Informação de empresa não encontrada',
          key,
        });
      });
  }

  function handleChangeCNPJ(cnpj: any) {
    form.setFieldsValue({ cnpj: cnpjMask(cnpj) });

    const cleaned_cnpj = cleanMask(cnpj);

    if (cleaned_cnpj.length === 14) {
      handleFetchCNPJ(cleaned_cnpj);
    }
  }

  const getSufixo = (value: string) => {
    const n = parseInt(value);
    if (!n) return '';
    return `${n} ${n === 1 ? 'mês' : 'meses'}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Seção: Dados da Empresa */}
      <Card title="Dados da Empresa" size="small">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="cnpj"
              label="CNPJ"
              rules={[{ required: true, message: default_message }]}
            >
              <Input onChange={e => handleChangeCNPJ(e.target.value)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="nome"
              label="Nome"
              rules={[{ required: true, message: default_message }]}
            >
              <Input maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="nome_fantasia" label="Nome Fantasia">
              <Input maxLength={150} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="razao_social" label="Razão Social">
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="inscricao_estadual" label="Inscrição Estadual">
              <Input maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="id_ramo_atividade"
              label="Ramo de Atividade"
              rules={[{ required: true, message: default_message }]}
            >
              <Select
                options={ramoAtividadeOptions}
                loading={loading.ramo_atividade}
                dropdownRender={menu => (
                  <div>
                    {menu}
                    <Divider style={{ margin: '4px 0' }} />
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        padding: 8,
                      }}
                    >
                      <Input
                        style={{ flex: 'auto' }}
                        value={newRamoAtividade}
                        onChange={val => setNewRamoAtividade(val.target.value)}
                        onPressEnter={() => handleStoreRamoAtividade()}
                        placeholder="Insira um novo Ramo de Atividade"
                      />
                    </div>
                  </div>
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="data_abertura"
              label="Data de Abertura do Estabelecimento"
            >
              <Input type="date" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Seção: Contato */}
      <Card title="Informações de Contato" size="small">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="telefone" label="Telefone">
              <Input
                onChange={e =>
                  form.setFieldsValue({
                    telefone: celularMask(e.target.value),
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="fax" label="FAX">
              <Input maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: default_message },
                { type: 'email', message: 'Insira um email válido' },
              ]}
            >
              <Input maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="celular_estabelecimento"
              label="Celular do Estabelecimento"
            >
              <Input maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={24}>
            <Form.Item
              name="ponto_referencia"
              label="Ponto de Referência do EC"
            >
              <Input maxLength={150} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Seção: Funcionamento */}
      <Card title="Funcionamento do Estabelecimento" size="small">
        <Row gutter={16}>
          <Col xs={24} md={24}>
            <Form.Item
              name="horario_funcionamento"
              label="Horário de Funcionamento"
              rules={[{ required: true, message: default_message }]}
            >
              <Select
                options={options_horario_funcionamento}
                showSearch
                filterOption={(input, option) =>
                  option?.label
                    .toLowerCase()
                    .includes(input.toLowerCase()) as boolean
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Seção: Tipo de Estabelecimento - Box separado */}
      <Card
        title="Tipo de Estabelecimento"
        size="small"
        style={{
          border: '2px solid #1890ff',
          backgroundColor: '#f6ffed',
        }}
      >
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Estabelecimento fica em um shopping?"
              name="estabelecimento_shopping"
              rules={[{ required: true, message: default_message }]}
            >
              <Radio.Group
                options={[
                  { label: 'Sim', value: true },
                  { label: 'Não', value: false },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Seção: Configurações Técnicas */}
      <Card title="Configurações Técnicas e Financeiras" size="small">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="tipo_tecnologia"
              label="Tipo de Tecnologia"
              rules={[{ required: true, message: default_message }]}
            >
              <Select options={options_tipo_tecnologia} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="quantidade_mids"
              label="Quantidade de MIDs"
              rules={[
                { required: true, message: default_message },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    if (Number(value) > 100) {
                      return Promise.reject(
                        'A quantidade não pode ser maior que 100',
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="number" min={0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="preco_aluguel"
              label="Preço de Aluguel do Dispositivo"
              rules={[{ required: true, message: default_message }]}
            >
              <Input prefix="R$" type="number" min={0} step="0.01" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="periodo_isencao"
              label="Período de Isenção"
              rules={[
                {
                  required: true,
                  message: default_message,
                },
                {
                  validator: (_, value) => {
                    const numero = parseInt(
                      value?.toString().replace(/\D/g, ''),
                      10,
                    );
                    if (isNaN(numero)) {
                      return Promise.reject('Valor inválido');
                    }
                    if (numero > 24) {
                      return Promise.reject(
                        'O período não pode ser maior que 24 meses',
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={event => {
                  form.setFieldsValue({
                    periodo_isencao: getSufixo(
                      event.target.value.replace(/\D/g, ''),
                    ),
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default FormJuridico;
